import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Bell,
  Bike,
  CheckCircle2,
  Clock3,
  Download,
  Edit3,
  Filter,
  LayoutDashboard,
  LocateFixed,
  LogOut,
  MapPin,
  Navigation,
  Phone,
  Search,
  ShieldCheck,
  Truck,
  UserCircle2,
  Wallet,
} from 'lucide-react';
import type { User } from '../../App';
import { api } from '../../lib/api';

interface DeliveryPartnerDashboardProps {
  user: User;
  onLogout: () => void;
  onOpenApplication?: () => void;
}

interface QueueOrder {
  id: string;
  orderNumber: string;
  status: string;
  total: number;
  deliveryFee: number;
  deliveryAddress: string;
  assignedDeliveryPartnerId: string | null;
  pickupConfirmedAt?: string | null;
  deliveryConfirmedAt?: string | null;
  placedAt?: string;
  restaurant: {
    id: string;
    name: string;
    address: string;
    latitude?: number | null;
    longitude?: number | null;
  };
  customer: {
    id: string;
    name: string;
    phone?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
  distance?: {
    toRestaurantKm?: number | null;
    restaurantToCustomerKm?: number | null;
    totalTripKm?: number | null;
    etaMinutes?: number | null;
  };
}

type PartnerProfile = {
  vehicleType: 'Bike' | 'Cycle' | 'Scooter';
  vehicleNumber: string;
  deliveryArea: string;
  aadhaarFileName: string;
  aadhaarFileData: string;
  licenseFileName: string;
  licenseFileData: string;
  bankAccountName: string;
  bankName: string;
  branchName: string;
  bankAccountNumber: string;
  bankIfsc: string;
};

type DeliveryApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

type DeliveryApplicationRecord = {
  id: string;
  status: DeliveryApplicationStatus;
  vehicleType: string;
  vehicleNumber?: string | null;
  licenseNumber?: string | null;
  drivingLicenseUrl?: string | null;
  drivingLicenseName?: string | null;
  deliveryArea?: string | null;
  idProofUrl?: string | null;
  idProofName?: string | null;
  bankAccountName?: string | null;
  bankName?: string | null;
  branchName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  reviewNotes?: string | null;
  reviewedAt?: string | null;
  activatedAt?: string | null;
};

type DashboardTab = 'dashboard' | 'queue' | 'active' | 'history' | 'insights' | 'profile';
type QueueSort = 'eta' | 'earning' | 'distance';
type DistanceFilter = 'all' | 'near' | 'medium' | 'far';

const profileStorageKey = (userId: string) => `delivery_partner_profile_${userId}`;
const historyStorageKey = (userId: string) => `delivery_partner_history_${userId}`;

const defaultProfile = (user: User): PartnerProfile => ({
  vehicleType: 'Bike',
  vehicleNumber: '',
  deliveryArea: '',
  aadhaarFileName: '',
  aadhaarFileData: '',
  licenseFileName: '',
  licenseFileData: '',
  bankAccountName: user.name || '',
  bankName: '',
  branchName: '',
  bankAccountNumber: '',
  bankIfsc: '',
});

const formatStatus = (status: string) =>
  String(status || '')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatKm = (value?: number | null) =>
  value !== null && value !== undefined && Number.isFinite(value) ? `${value.toFixed(1)} km` : 'NA';

const formatSecondsAgo = (timestampMs: number | null, nowMs: number): string => {
  if (!timestampMs) return '';
  const diff = Math.max(0, Math.floor((nowMs - timestampMs) / 1000));
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const minutes = Math.floor(diff / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

export function DeliveryPartnerDashboard({
  user,
  onLogout,
  onOpenApplication,
}: DeliveryPartnerDashboardProps) {
  const [isOnline, setIsOnline] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<string[]>([]);
  const [queueOrders, setQueueOrders] = useState<QueueOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationLabel, setLocationLabel] = useState('');
  const [locationUpdatedAt, setLocationUpdatedAt] = useState<number | null>(null);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'resolving' | 'error'>('idle');
  const [locationError, setLocationError] = useState('');
  const [history, setHistory] = useState<QueueOrder[]>([]);
  const [activeTab, setActiveTab] = useState<DashboardTab>('dashboard');
  const [searchText, setSearchText] = useState('');
  const [historyRange, setHistoryRange] = useState<'today' | 'week' | 'month'>('week');
  const [queueSort, setQueueSort] = useState<QueueSort>('eta');
  const [distanceFilter, setDistanceFilter] = useState<DistanceFilter>('all');
  const [minEarningFilter, setMinEarningFilter] = useState(0);
  const [shiftElapsedSeconds, setShiftElapsedSeconds] = useState(0);
  const [onlineSince, setOnlineSince] = useState<Date | null>(null);
  const [currentClock, setCurrentClock] = useState<Date>(() => new Date());
  const [application, setApplication] = useState<DeliveryApplicationRecord | null>(null);
  const [profile] = useState<PartnerProfile>(() => {
    try {
      const raw = localStorage.getItem(profileStorageKey(user.id));
      return raw ? { ...defaultProfile(user), ...JSON.parse(raw) } : defaultProfile(user);
    } catch {
      return defaultProfile(user);
    }
  });

  const isApplicationApproved = application?.status === 'APPROVED';
  const isApplicationPending = application?.status === 'PENDING';

  useEffect(() => {
    try {
      const raw = localStorage.getItem(historyStorageKey(user.id));
      setHistory(raw ? JSON.parse(raw) : []);
    } catch {
      setHistory([]);
    }
  }, [user.id]);

  useEffect(() => {
    const loadApplication = async () => {
      try {
        const response = await api.deliveryApplications.getMine();
        const record = response.data || null;
        setApplication(record);

        if (record) {
          const nextProfile: PartnerProfile = {
            ...defaultProfile(user),
              vehicleType: (record.vehicleType as PartnerProfile['vehicleType']) || 'Bike',
              vehicleNumber: record.vehicleNumber || '',
              deliveryArea: record.deliveryArea || '',
            aadhaarFileName: record.idProofName || '',
            aadhaarFileData: record.idProofUrl || '',
            licenseFileName: record.drivingLicenseName || '',
            licenseFileData: record.drivingLicenseUrl || '',
            bankAccountName: record.bankAccountName || user.name || '',
            bankName: record.bankName || '',
            branchName: record.branchName || '',
            bankAccountNumber: record.bankAccountNumber || '',
            bankIfsc: record.bankIfsc || '',
          };
          localStorage.setItem(profileStorageKey(user.id), JSON.stringify(nextProfile));
        }
      } catch {
        setApplication(null);
      }
    };

    void loadApplication();
  }, [user]);

  const saveHistory = (items: QueueOrder[]) => {
    setHistory(items);
    localStorage.setItem(historyStorageKey(user.id), JSON.stringify(items));
  };

  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported in this browser.');
      setLocationStatus('error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationError('');
        setLocationStatus('idle');
        setLocationUpdatedAt(Date.now());
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        setLocationUpdatedAt(Date.now());
        setLocationStatus('error');
        setLocationError(error?.message || 'Unable to access your location.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 10000 }
    );
  };

  useEffect(() => {
    fetchCurrentLocation();
    const timer = setInterval(fetchCurrentLocation, 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!currentLocation) return;

    const roundedKey = `${currentLocation.latitude.toFixed(3)},${currentLocation.longitude.toFixed(3)}`;
    const cacheKey = `delivery_partner_location_label_${roundedKey}`;

    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setLocationLabel(cached);
        return;
      }
    } catch {
      // ignore cache read errors
    }

    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), 2500);

    const resolveLabel = async () => {
      try {
        setLocationStatus('resolving');
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
          currentLocation.latitude
        )}&lon=${encodeURIComponent(currentLocation.longitude)}&zoom=14&addressdetails=1`;
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { 'Accept-Language': 'en' },
          referrerPolicy: 'no-referrer',
        });
        if (!response.ok) throw new Error('Reverse geocoding failed');
        const data: any = await response.json();
        const address = data?.address || {};
        const nextLabel =
          [
            address.suburb,
            address.neighbourhood,
            address.village,
            address.town,
            address.city_district,
            address.city,
          ]
            .map((value: any) => String(value || '').trim())
            .filter(Boolean)
            .slice(0, 1)
            .concat(
              [address.city, address.town, address.village]
                .map((value: any) => String(value || '').trim())
                .filter(Boolean)
                .slice(0, 1)
            )
            .concat(
              [address.state]
                .map((value: any) => String(value || '').trim())
                .filter(Boolean)
                .slice(0, 1)
            )
            .filter(Boolean)
            .join(', ') ||
          String(data?.display_name || '').trim();

        if (mounted && nextLabel) {
          setLocationLabel(nextLabel);
          setLocationStatus('idle');
          try {
            localStorage.setItem(cacheKey, nextLabel);
          } catch {
            // ignore cache write errors
          }
        }
      } catch (error: any) {
        if (!mounted) return;
        if (String(error?.name || '') === 'AbortError') {
          setLocationStatus('idle');
          return;
        }
        setLocationStatus('idle');
        // Keep coordinates as fallback if reverse-geocoding fails.
        setLocationLabel('');
      } finally {
        clearTimeout(timeoutHandle);
      }
    };

    void resolveLabel();
    return () => {
      mounted = false;
      clearTimeout(timeoutHandle);
      controller.abort();
    };
  }, [currentLocation?.latitude, currentLocation?.longitude]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isOnline && !onlineSince) {
      setOnlineSince(new Date());
      return;
    }

    if (!isOnline && onlineSince) {
      const elapsed = Math.max(0, Math.floor((Date.now() - onlineSince.getTime()) / 1000));
      setShiftElapsedSeconds((prev) => prev + elapsed);
      setOnlineSince(null);
    }
  }, [isOnline, onlineSince]);

  const loadQueue = async () => {
    if (!isOnline || !isApplicationApproved) return;
    setLoading(true);
    try {
      const response = await api.orders.getDeliveryQueue(
        currentLocation
          ? { latitude: currentLocation.latitude, longitude: currentLocation.longitude }
          : undefined
      );
      const data = Array.isArray(response?.data) ? (response.data as QueueOrder[]) : [];

      const previousIds = new Set(queueOrders.map((order) => order.id));
      const incoming = data.filter((order) => !previousIds.has(order.id));
      if (incoming.length > 0) {
        const incomingNotifications = incoming.map(
          (order) => `New ${order.orderNumber} from ${order.restaurant.name}`
        );
        setNotifications((prev) => [...incomingNotifications, ...prev].slice(0, 30));
      }

      setQueueOrders(data);
    } catch {
      // no-op
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadQueue();
    const timer = setInterval(() => void loadQueue(), 10000);
    return () => clearInterval(timer);
  }, [isOnline, isApplicationApproved, currentLocation?.latitude, currentLocation?.longitude]);

  const assignedOrders = useMemo(
    () => queueOrders.filter((order) => String(order.assignedDeliveryPartnerId || '') === user.id),
    [queueOrders, user.id]
  );

  const pendingQueueOrders = useMemo(
    () => queueOrders.filter((order) => !order.assignedDeliveryPartnerId),
    [queueOrders]
  );

  const deliveredHistory = useMemo(
    () => history.filter((order) => String(order.status || '').toUpperCase() === 'DELIVERED'),
    [history]
  );

  const deliveredToday = useMemo(() => {
    const today = new Date().toDateString();
    return deliveredHistory.filter((order) => {
      const sourceDate = order.deliveryConfirmedAt || order.placedAt || '';
      if (!sourceDate) return false;
      const date = new Date(sourceDate);
      if (Number.isNaN(date.getTime())) return false;
      return date.toDateString() === today;
    });
  }, [deliveredHistory]);

  const earningsToday = useMemo(
    () => deliveredToday.reduce((sum, order) => sum + Number(order.deliveryFee || 0), 0),
    [deliveredToday]
  );

  const totalEarnings = useMemo(
    () => deliveredHistory.reduce((sum, order) => sum + Number(order.deliveryFee || 0), 0),
    [deliveredHistory]
  );

  const avgEarningPerTrip = useMemo(() => {
    if (!deliveredHistory.length) return 0;
    return totalEarnings / deliveredHistory.length;
  }, [deliveredHistory, totalEarnings]);

  const acceptanceRate = useMemo(() => {
    const accepted = deliveredHistory.length + assignedOrders.length;
    const rejected = history.filter((order) => String(order.status).toUpperCase() === 'REJECTED').length;
    const totalDecisions = accepted + rejected;
    if (!totalDecisions) return 0;
    return (accepted / totalDecisions) * 100;
  }, [deliveredHistory.length, assignedOrders.length, history]);

  const dailyEarningsSeries = useMemo(() => {
    const days = 7;
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - (days - 1));
    start.setHours(0, 0, 0, 0);

    const buckets = new Map<string, number>();
    for (let i = 0; i < days; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      buckets.set(day.toDateString(), 0);
    }

    for (const order of deliveredHistory) {
      const sourceDate = order.deliveryConfirmedAt || order.placedAt || '';
      if (!sourceDate) continue;
      const date = new Date(sourceDate);
      if (Number.isNaN(date.getTime())) continue;
      const key = date.toDateString();
      if (!buckets.has(key)) continue;
      buckets.set(key, (buckets.get(key) || 0) + Number(order.deliveryFee || 0));
    }

    return Array.from(buckets.entries()).map(([label, value]) => ({
      label: new Date(label).toLocaleDateString(undefined, { weekday: 'short' }),
      value,
    }));
  }, [deliveredHistory]);

  const hourlyDeliveredSeries = useMemo(() => {
    const buckets = Array.from({ length: 12 }, (_, index) => ({
      label: `${index + 9}`,
      value: 0,
    }));

    for (const order of deliveredToday) {
      const sourceDate = order.deliveryConfirmedAt || order.placedAt || '';
      if (!sourceDate) continue;
      const date = new Date(sourceDate);
      if (Number.isNaN(date.getTime())) continue;
      const hour = date.getHours();
      if (hour < 9 || hour > 20) continue;
      buckets[hour - 9].value += 1;
    }

    return buckets;
  }, [deliveredToday]);

  const recentOrders = useMemo(() => {
    const combined = [...assignedOrders, ...pendingQueueOrders, ...deliveredHistory]
      .filter(Boolean)
      .slice(0);
    const unique = new Map<string, QueueOrder>();
    for (const item of combined) {
      if (!unique.has(item.id)) unique.set(item.id, item);
    }
    return Array.from(unique.values())
      .sort((a, b) => {
        const aTime = new Date(a.placedAt || a.pickupConfirmedAt || a.deliveryConfirmedAt || 0).getTime() || 0;
        const bTime = new Date(b.placedAt || b.pickupConfirmedAt || b.deliveryConfirmedAt || 0).getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, 8);
  }, [assignedOrders, pendingQueueOrders, deliveredHistory]);

  const liveOnlineSeconds =
    isOnline && onlineSince ? Math.max(0, Math.floor((currentClock.getTime() - onlineSince.getTime()) / 1000)) : 0;
  const shiftSeconds = shiftElapsedSeconds + liveOnlineSeconds;
  const shiftHours = Math.floor(shiftSeconds / 3600);
  const shiftMinutes = Math.floor((shiftSeconds % 3600) / 60);
  const shiftDisplaySeconds = shiftSeconds % 60;

  const markHistory = (orderId: string, status: string) => {
    const current = queueOrders.find((order) => order.id === orderId);
    if (!current) return;
    const updatedItem = { ...current, status };
    const without = history.filter((item) => item.id !== orderId);
    saveHistory([updatedItem, ...without].slice(0, 120));
  };

  const runAction = async (orderId: string, action: () => Promise<any>) => {
    setActionBusyId(orderId);
    try {
      await action();
      await loadQueue();
    } catch (error: any) {
      alert(error?.message || 'Action failed.');
    } finally {
      setActionBusyId(null);
    }
  };

  const acceptOrder = async (orderId: string) => runAction(orderId, () => api.orders.acceptDelivery(orderId));

  const rejectOrder = async (orderId: string) =>
    runAction(orderId, async () => {
      await api.orders.rejectDelivery(orderId);
      markHistory(orderId, 'REJECTED');
    });

  const confirmPickup = async (orderId: string) =>
    runAction(orderId, () => api.orders.confirmPickup(orderId));

  const confirmDelivery = async (orderId: string) =>
    runAction(orderId, async () => {
      await api.orders.confirmDelivery(orderId);
      markHistory(orderId, 'DELIVERED');
    });

  const openDirections = (
    latitude?: number | null,
    longitude?: number | null,
    label?: string
  ) => {
    if (!Number.isFinite(latitude as number) || !Number.isFinite(longitude as number)) {
      if (label) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`, '_blank');
      }
      return;
    }
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`, '_blank');
  };

  const filteredQueue = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const base = query
      ? pendingQueueOrders.filter((order) => {
          const haystack = [order.orderNumber, order.restaurant.name, order.customer.name, order.deliveryAddress]
            .join(' ')
            .toLowerCase();
          return haystack.includes(query);
        })
      : pendingQueueOrders;

    const withFilters = base.filter((order) => {
      const tripKm = Number(order.distance?.totalTripKm || 0);
      const earning = Number(order.deliveryFee || 0);

      if (distanceFilter === 'near' && !(tripKm > 0 && tripKm <= 3)) return false;
      if (distanceFilter === 'medium' && !(tripKm > 3 && tripKm <= 7)) return false;
      if (distanceFilter === 'far' && !(tripKm > 7)) return false;
      if (earning < minEarningFilter) return false;
      return true;
    });

    const sorted = [...withFilters];
    sorted.sort((a, b) => {
      if (queueSort === 'earning') {
        return Number(b.deliveryFee || 0) - Number(a.deliveryFee || 0);
      }
      if (queueSort === 'distance') {
        return Number(a.distance?.totalTripKm || 9999) - Number(b.distance?.totalTripKm || 9999);
      }
      return Number(a.distance?.etaMinutes || 9999) - Number(b.distance?.etaMinutes || 9999);
    });
    return sorted;
  }, [pendingQueueOrders, searchText, queueSort, distanceFilter, minEarningFilter]);

  const filteredHistory = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now);
    if (historyRange === 'today') {
      cutoff.setHours(0, 0, 0, 0);
    } else if (historyRange === 'week') {
      cutoff.setDate(now.getDate() - 7);
    } else {
      cutoff.setDate(now.getDate() - 30);
    }

    const query = searchText.trim().toLowerCase();
    const rangeFiltered = history.filter((order) => {
      const sourceDate = order.deliveryConfirmedAt || order.placedAt || '';
      if (!sourceDate) return true;
      const date = new Date(sourceDate);
      if (Number.isNaN(date.getTime())) return true;
      return date.getTime() >= cutoff.getTime();
    });

    if (!query) return rangeFiltered;
    return rangeFiltered.filter((order) => {
      const haystack = [order.orderNumber, order.restaurant?.name || '', order.customer?.name || '', order.status]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [history, searchText, historyRange]);

  const exportHistoryCsv = () => {
    const rows = filteredHistory.map((order) => ({
      orderNumber: order.orderNumber,
      restaurant: order.restaurant?.name || '',
      customer: order.customer?.name || '',
      status: String(order.status || ''),
      deliveryFee: Number(order.deliveryFee || 0).toFixed(2),
      totalTripKm: Number(order.distance?.totalTripKm || 0).toFixed(2),
      etaMinutes: Number(order.distance?.etaMinutes || 0),
    }));
    const headers = ['orderNumber', 'restaurant', 'customer', 'status', 'deliveryFee', 'totalTripKm', 'etaMinutes'];
    const csv = [
      headers.join(','),
      ...rows.map((row) =>
        headers.map((header) => `"${String((row as any)[header] ?? '').replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `delivery-history-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderOrderCard = (order: QueueOrder, mode: 'queue' | 'active') => (
    <article key={order.id} className="rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-gray-900 font-semibold">{order.orderNumber}</p>
          <p className="text-sm text-gray-600">{order.restaurant.name}</p>
          <p className="text-xs text-gray-500 mt-1">{order.customer.name} • {formatStatus(order.status)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Potential Earning</p>
          <p className="text-base font-semibold text-green-600">Rs {Number(order.deliveryFee || 0).toFixed(0)}</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="rounded-lg bg-blue-50 text-blue-700 px-2 py-1.5">To Restaurant: {formatKm(order.distance?.toRestaurantKm)}</div>
        <div className="rounded-lg bg-indigo-50 text-indigo-700 px-2 py-1.5">To Customer: {formatKm(order.distance?.restaurantToCustomerKm)}</div>
        <div className="rounded-lg bg-purple-50 text-purple-700 px-2 py-1.5">Trip: {formatKm(order.distance?.totalTripKm)}</div>
        <div className="rounded-lg bg-amber-50 text-amber-700 px-2 py-1.5">ETA: {order.distance?.etaMinutes ? `${order.distance.etaMinutes} mins` : 'NA'}</div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        <button
          onClick={() => openDirections(order.restaurant.latitude, order.restaurant.longitude, order.restaurant.address)}
          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm flex items-center justify-center gap-2"
        >
          <MapPin className="w-4 h-4" /> Restaurant
        </button>
        <button
          onClick={() => openDirections(order.customer.latitude, order.customer.longitude, order.deliveryAddress)}
          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm flex items-center justify-center gap-2"
        >
          <Navigation className="w-4 h-4" /> Customer
        </button>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        {mode === 'queue' ? (
          <>
            <button
              onClick={() => void acceptOrder(order.id)}
              disabled={actionBusyId === order.id}
              className="px-3 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm disabled:opacity-60"
            >
              {actionBusyId === order.id ? 'Please wait...' : 'Accept Order'}
            </button>
            <button
              onClick={() => void rejectOrder(order.id)}
              disabled={actionBusyId === order.id}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm disabled:opacity-60"
            >
              Reject Order
            </button>
          </>
        ) : (
          <>
            <div className="md:col-span-2 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-700">
              {order.deliveryConfirmedAt
                ? 'Progress: Delivered'
                : order.pickupConfirmedAt
                ? 'Progress: Picked Up -> On The Way'
                : 'Progress: Accepted -> Go to Restaurant'}
            </div>
            <button
              onClick={() => void confirmPickup(order.id)}
              disabled={actionBusyId === order.id}
              className="px-3 py-2 rounded-lg bg-amber-100 text-amber-700 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <CheckCircle2 className="w-4 h-4" /> Pickup Confirm
            </button>
            <button
              onClick={() => void confirmDelivery(order.id)}
              disabled={actionBusyId === order.id}
              className="px-3 py-2 rounded-lg bg-green-100 text-green-700 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <ShieldCheck className="w-4 h-4" /> Delivery Confirm
            </button>
          </>
        )}
      </div>

      {order.customer.phone && (
        <a href={`tel:${order.customer.phone}`} className="mt-2 inline-flex items-center gap-2 text-sm text-blue-700">
          <Phone className="w-4 h-4" /> Call Customer
        </a>
      )}
    </article>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
      <header className="sticky top-0 z-30 border-b border-indigo-200/60 backdrop-blur bg-white/85">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">Delivery Partner</p>
            <h1 className="text-xl font-bold text-gray-900 truncate">{user.name}</h1>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNotifications((value) => !value)}
              className="relative p-2 rounded-lg border border-indigo-200 bg-white hover:bg-indigo-50"
            >
              <Bell className="w-5 h-5 text-indigo-700" />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 text-[10px] bg-red-500 text-white rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                  {notifications.length}
                </span>
              )}
            </button>
            <label className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-white px-3 py-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isOnline}
                onChange={(event) => setIsOnline(event.target.checked)}
                className="sr-only peer"
                aria-label="Toggle partner online status"
              />
              <span
                className={`relative h-7 w-14 rounded-full transition-colors duration-200 ${
                  isOnline ? 'bg-green-500' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    isOnline ? 'translate-x-7' : 'translate-x-0.5'
                  }`}
                />
              </span>
              <span className={`text-sm font-medium ${isOnline ? 'text-green-700' : 'text-gray-600'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </label>
            <button
              onClick={onLogout}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {showNotifications && (
        <div className="max-w-7xl mx-auto px-4 pt-3">
          <div className="bg-white rounded-2xl border border-indigo-100 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">Notification Panel</h2>
              <button
                onClick={() => setNotifications([])}
                className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-700"
              >
                Clear
              </button>
            </div>
            {notifications.length === 0 ? (
              <p className="text-sm text-gray-500">No new notifications.</p>
            ) : (
              <div className="space-y-2">
                {notifications.slice(0, 8).map((item, index) => (
                  <div key={`${item}-${index}`} className="text-sm text-gray-700 bg-indigo-50 rounded-lg p-2">
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {activeTab !== 'dashboard' && (
          <section className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">Queue</p>
              <p className="text-2xl font-bold text-gray-900">{pendingQueueOrders.length}</p>
            </div>
            <div className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">Active Trips</p>
              <p className="text-2xl font-bold text-blue-700">{assignedOrders.length}</p>
            </div>
            <div className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">Delivered</p>
              <p className="text-2xl font-bold text-green-700">{deliveredHistory.length}</p>
            </div>
            <div className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">Earnings</p>
              <p className="text-2xl font-bold text-emerald-700">Rs {totalEarnings.toFixed(0)}</p>
            </div>
            <div className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">Shift</p>
              <p className="text-sm font-semibold text-gray-900">
                {String(shiftHours).padStart(2, '0')}h {String(shiftMinutes).padStart(2, '0')}m {String(shiftDisplaySeconds).padStart(2, '0')}s
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm">
              <p className="text-xs text-gray-500">Location</p>
              <p className="text-sm font-semibold text-gray-800 truncate" title={locationLabel || undefined}>
                {currentLocation
                  ? locationStatus === 'resolving'
                    ? 'Updating...'
                    : locationLabel ||
                      `${currentLocation.latitude.toFixed(4)}, ${currentLocation.longitude.toFixed(4)}`
                  : 'Unavailable'}
              </p>
              <p className="text-[11px] text-gray-500 mt-1 truncate" title={locationError || undefined}>
                {locationError
                  ? locationError
                  : formatSecondsAgo(locationUpdatedAt, currentClock.getTime())
                  ? `Updated ${formatSecondsAgo(locationUpdatedAt, currentClock.getTime())}`
                  : ''}
              </p>
            </div>
          </section>
        )}

        <section className="grid grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)] gap-4">
          <aside className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm h-fit">
            <p className="text-xs uppercase tracking-[0.24em] text-indigo-500 mb-3">Workspace</p>
              <div className="space-y-2">
                {[
                  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                  { id: 'queue', label: 'Order Queue', icon: Bike },
                  { id: 'active', label: 'Active Trips', icon: Truck },
                  { id: 'history', label: 'History', icon: Clock3 },
                  { id: 'insights', label: 'Insights', icon: BarChart3 },
                  { id: 'profile', label: 'Profile', icon: UserCircle2 },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as DashboardTab)}
                    className={`w-full px-3 py-3 rounded-xl text-sm flex items-center gap-3 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{tab.label}</span>
                    {tab.id === 'queue' && pendingQueueOrders.length > 0 && (
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                          activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700'
                        }`}
                      >
                        {pendingQueueOrders.length}
                      </span>
                    )}
                    {tab.id === 'active' && assignedOrders.length > 0 && (
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                          activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {assignedOrders.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            <div className="mt-4 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 p-3">
              <p className="text-xs text-gray-500 mb-1">Working Shift</p>
              <p className="text-lg font-semibold text-gray-900">
                {String(shiftHours).padStart(2, '0')}h {String(shiftMinutes).padStart(2, '0')}m {String(shiftDisplaySeconds).padStart(2, '0')}s
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {isOnline ? 'Counting while online' : 'Timer paused while offline'}
              </p>
            </div>
          </aside>

          <div className="space-y-4">
            {activeTab === 'dashboard' ? (
              <section className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs text-gray-500">Quick actions</p>
                  <p className="text-sm font-semibold text-gray-900">Stay on top of your shift</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveTab('queue')}
                    className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium"
                  >
                    Open Queue
                  </button>
                  <button
                    onClick={() => {
                      void loadQueue();
                      fetchCurrentLocation();
                    }}
                    className="px-3 py-2 rounded-lg bg-indigo-100 text-indigo-700 text-sm flex items-center justify-center gap-2 font-medium"
                  >
                    <LocateFixed className="w-4 h-4" /> Refresh
                  </button>
                </div>
              </section>
            ) : (
              <section className="rounded-2xl bg-white border border-indigo-100 p-3 shadow-sm flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                  <div className="relative w-full md:w-72">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search orders"
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm"
                    />
                  </div>
                  {activeTab === 'queue' && (
                    <>
                      <div className="relative">
                        <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <select
                          value={queueSort}
                          onChange={(e) => setQueueSort(e.target.value as QueueSort)}
                          className="pl-9 pr-8 py-2 rounded-lg border border-gray-300 text-sm bg-white"
                        >
                          <option value="eta">Sort by ETA</option>
                          <option value="earning">Sort by Earning</option>
                          <option value="distance">Sort by Distance</option>
                        </select>
                      </div>
                      <select
                        value={distanceFilter}
                        onChange={(e) => setDistanceFilter(e.target.value as DistanceFilter)}
                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white"
                      >
                        <option value="all">All Distances</option>
                        <option value="near">Near (0-3 km)</option>
                        <option value="medium">Medium (3-7 km)</option>
                        <option value="far">Far (7+ km)</option>
                      </select>
                      <select
                        value={minEarningFilter}
                        onChange={(e) => setMinEarningFilter(Number(e.target.value))}
                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white"
                      >
                        <option value={0}>Any Earning</option>
                        <option value={10}>Min Rs 10</option>
                        <option value={20}>Min Rs 20</option>
                        <option value={40}>Min Rs 40</option>
                      </select>
                    </>
                  )}
                </div>
                <button
                  onClick={() => {
                    void loadQueue();
                    fetchCurrentLocation();
                  }}
                  className="px-3 py-2 rounded-lg bg-indigo-100 text-indigo-700 text-sm flex items-center justify-center gap-2"
                >
                  <LocateFixed className="w-4 h-4" /> Refresh
                </button>
              </section>
            )}

            {loading && (
              <section className="rounded-2xl bg-white border border-indigo-100 p-4 text-sm text-gray-500">Refreshing orders...</section>
            )}

            {activeTab === 'dashboard' && (
              <section className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
                    <p className="text-xs text-gray-500">Order Queue</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{pendingQueueOrders.length}</p>
                    <p className="mt-2 text-xs text-gray-500">Unassigned orders nearby</p>
                  </div>
                  <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
                    <p className="text-xs text-gray-500">Active Trips</p>
                    <p className="mt-1 text-2xl font-bold text-blue-700">{assignedOrders.length}</p>
                    <p className="mt-2 text-xs text-gray-500">Accepted & in-progress</p>
                  </div>
                  <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
                    <p className="text-xs text-gray-500">Delivered Today</p>
                    <p className="mt-1 text-2xl font-bold text-emerald-700">{deliveredToday.length}</p>
                    <p className="mt-2 text-xs text-gray-500">Trips completed today</p>
                  </div>
                  <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
                    <p className="text-xs text-gray-500">Earnings Today</p>
                    <p className="mt-1 text-2xl font-bold text-emerald-700">Rs {earningsToday.toFixed(0)}</p>
                    <p className="mt-2 text-xs text-gray-500">Delivery fees earned</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-white border border-indigo-100 p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">Today's Summary</h2>
                        <p className="text-xs text-gray-500">Deliveries by hour (9am–8pm)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                            isOnline ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <span className={`h-2 w-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                        <span className="text-xs text-gray-500">{currentClock.toLocaleTimeString()}</span>
                      </div>
                    </div>
                    <div className="h-32 flex items-end gap-2">
                      {(() => {
                        const max = Math.max(1, ...hourlyDeliveredSeries.map((d) => d.value));
                        return hourlyDeliveredSeries.map((d) => (
                          <div key={d.label} className="flex-1 flex flex-col items-center gap-2">
                            <div className="w-full h-20 flex items-end rounded-lg bg-indigo-50 overflow-hidden">
                              <div
                                className="w-full bg-gradient-to-t from-indigo-600 to-blue-500"
                                style={{ height: `${Math.round((d.value / max) * 100)}%`, minHeight: d.value ? 8 : 2 }}
                              />
                            </div>
                            <span className="text-[10px] text-gray-500">{d.label}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white border border-indigo-100 p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">Earnings Overview</h2>
                        <p className="text-xs text-gray-500">Last 7 days</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-sm font-semibold text-emerald-700">Rs {totalEarnings.toFixed(0)}</p>
                      </div>
                    </div>
                    <div className="h-32">
                      {(() => {
                        const values = dailyEarningsSeries.map((d) => d.value);
                        const max = Math.max(1, ...values);
                        const w = 560;
                        const h = 120;
                        const padX = 24;
                        const padY = 14;
                        const toX = (index: number) =>
                          padX + (index * (w - padX * 2)) / Math.max(1, dailyEarningsSeries.length - 1);
                        const toY = (value: number) =>
                          h - padY - (value * (h - padY * 2)) / max;
                        const points = dailyEarningsSeries
                          .map((d, i) => `${toX(i).toFixed(1)},${toY(d.value).toFixed(1)}`)
                          .join(' ');

                        return (
                          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full">
                            <defs>
                              <linearGradient id="earnLine" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0" stopColor="#6366f1" />
                                <stop offset="1" stopColor="#3b82f6" />
                              </linearGradient>
                              <linearGradient id="earnFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#6366f1" stopOpacity="0.25" />
                                <stop offset="1" stopColor="#3b82f6" stopOpacity="0.02" />
                              </linearGradient>
                            </defs>
                            <polyline
                              points={`${points} ${toX(dailyEarningsSeries.length - 1)},${h - padY} ${toX(0)},${h - padY}`}
                              fill="url(#earnFill)"
                              stroke="none"
                            />
                            <polyline points={points} fill="none" stroke="url(#earnLine)" strokeWidth="4" strokeLinecap="round" />
                            {dailyEarningsSeries.map((d, i) => (
                              <circle key={d.label} cx={toX(i)} cy={toY(d.value)} r="5" fill="#fff" stroke="#3b82f6" strokeWidth="3" />
                            ))}
                          </svg>
                        );
                      })()}
                    </div>
                    <div className="mt-3 flex justify-between text-[11px] text-gray-500">
                      {dailyEarningsSeries.map((d) => (
                        <span key={d.label}>{d.label}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="rounded-2xl bg-white border border-indigo-100 p-5 shadow-sm lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-base font-semibold text-gray-900">Recent Orders</h2>
                      <button
                        onClick={() => setActiveTab('queue')}
                        className="text-xs font-semibold text-indigo-700 hover:text-indigo-900"
                      >
                        View Queue
                      </button>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {recentOrders.length === 0 ? (
                        <div className="py-8 text-center text-sm text-gray-500">No recent orders yet.</div>
                      ) : (
                        recentOrders.map((order) => (
                          <div key={order.id} className="py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{order.orderNumber}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {order.restaurant?.name} • {order.customer?.name}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">{formatStatus(order.status)}</p>
                              <p className="text-sm font-semibold text-emerald-700">Rs {Number(order.deliveryFee || 0).toFixed(0)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white border border-indigo-100 p-5 shadow-sm">
                    <h2 className="text-base font-semibold text-gray-900 mb-4">Shift</h2>
                    <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 p-4">
                      <p className="text-xs text-gray-500 mb-2">Shift Timer</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {String(shiftHours).padStart(2, '0')}:{String(shiftMinutes).padStart(2, '0')}:{String(shiftDisplaySeconds).padStart(2, '0')}
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        {isOnline ? 'Counting while online' : 'Paused while offline'}
                      </p>
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-white border border-gray-100 p-3">
                          <p className="text-[11px] text-gray-500">Acceptance</p>
                          <p className="text-sm font-semibold text-gray-900">{acceptanceRate.toFixed(0)}%</p>
                        </div>
                        <div className="rounded-lg bg-white border border-gray-100 p-3">
                          <p className="text-[11px] text-gray-500">Avg / Trip</p>
                          <p className="text-sm font-semibold text-gray-900">Rs {avgEarningPerTrip.toFixed(0)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'queue' && (
              <section className="space-y-4">
                {filteredQueue.length === 0 ? (
                  <div className="rounded-2xl bg-white border border-indigo-100 p-6 text-sm text-gray-500 text-center">
                    No new orders in queue right now.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 space-y-3">
                      {filteredQueue.map((order) => renderOrderCard(order, 'queue'))}
                    </div>
                    <div className="rounded-2xl bg-white border border-indigo-100 p-5 shadow-sm h-fit">
                      <h2 className="text-base font-semibold text-gray-900 mb-1">Map Preview</h2>
                      <p className="text-xs text-gray-500 mb-4">Route overview for the next best order</p>

                      <div className="h-44 rounded-2xl border border-gray-100 bg-gradient-to-br from-indigo-50 via-blue-50 to-white relative overflow-hidden">
                        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#6366f1_0,transparent_60%),radial-gradient(circle_at_80%_60%,#3b82f6_0,transparent_55%)]" />
                        <div className="absolute left-4 top-4 rounded-full bg-white/80 backdrop-blur px-3 py-1 text-xs font-semibold text-indigo-700 border border-indigo-100">
                          {formatKm(filteredQueue[0]?.distance?.totalTripKm)} • {filteredQueue[0]?.distance?.etaMinutes ? `${filteredQueue[0].distance.etaMinutes} mins` : 'NA'}
                        </div>
                        <div className="absolute bottom-4 left-4 right-4 grid grid-cols-2 gap-2 text-xs">
                          <div className="rounded-xl bg-white/85 backdrop-blur border border-gray-100 p-2">
                            <p className="text-gray-500">Pickup</p>
                            <p className="text-gray-900 font-semibold truncate">{filteredQueue[0]?.restaurant?.name}</p>
                          </div>
                          <div className="rounded-xl bg-white/85 backdrop-blur border border-gray-100 p-2">
                            <p className="text-gray-500">Drop</p>
                            <p className="text-gray-900 font-semibold truncate">{filteredQueue[0]?.customer?.name}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 space-y-3 text-sm">
                        <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                          <p className="text-xs text-gray-500">Order</p>
                          <p className="font-semibold text-gray-900">{filteredQueue[0]?.orderNumber}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{filteredQueue[0]?.deliveryAddress}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() =>
                              openDirections(
                                filteredQueue[0]?.restaurant?.latitude,
                                filteredQueue[0]?.restaurant?.longitude,
                                filteredQueue[0]?.restaurant?.address
                              )
                            }
                            className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm flex items-center justify-center gap-2"
                          >
                            <MapPin className="w-4 h-4" /> Pickup
                          </button>
                          <button
                            onClick={() =>
                              openDirections(
                                filteredQueue[0]?.customer?.latitude,
                                filteredQueue[0]?.customer?.longitude,
                                filteredQueue[0]?.deliveryAddress
                              )
                            }
                            className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm flex items-center justify-center gap-2"
                          >
                            <Navigation className="w-4 h-4" /> Drop
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {activeTab === 'active' && (
              <section className="space-y-4">
                {assignedOrders.length === 0 ? (
                  <div className="rounded-2xl bg-white border border-indigo-100 p-6 text-sm text-gray-500 text-center">
                    No active trips assigned to you.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 space-y-3">
                      {assignedOrders.map((order) => renderOrderCard(order, 'active'))}
                    </div>
                    <div className="rounded-2xl bg-white border border-indigo-100 p-5 shadow-sm h-fit">
                      <h2 className="text-base font-semibold text-gray-900 mb-1">Active Trip</h2>
                      <p className="text-xs text-gray-500 mb-4">Live delivery details</p>

                      <div className="h-44 rounded-2xl border border-gray-100 bg-gradient-to-br from-emerald-50 via-blue-50 to-white relative overflow-hidden">
                        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#10b981_0,transparent_60%),radial-gradient(circle_at_80%_60%,#3b82f6_0,transparent_55%)]" />
                        <div className="absolute left-4 top-4 rounded-full bg-white/80 backdrop-blur px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100">
                          {formatKm(assignedOrders[0]?.distance?.totalTripKm)} • {assignedOrders[0]?.distance?.etaMinutes ? `${assignedOrders[0].distance.etaMinutes} mins` : 'NA'}
                        </div>
                        <div className="absolute bottom-4 left-4 right-4 grid grid-cols-2 gap-2 text-xs">
                          <div className="rounded-xl bg-white/85 backdrop-blur border border-gray-100 p-2">
                            <p className="text-gray-500">Pickup</p>
                            <p className="text-gray-900 font-semibold truncate">{assignedOrders[0]?.restaurant?.name}</p>
                          </div>
                          <div className="rounded-xl bg-white/85 backdrop-blur border border-gray-100 p-2">
                            <p className="text-gray-500">Drop</p>
                            <p className="text-gray-900 font-semibold truncate">{assignedOrders[0]?.customer?.name}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 space-y-3">
                        <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm">
                          <p className="text-xs text-gray-500">Customer</p>
                          <p className="font-semibold text-gray-900">{assignedOrders[0]?.customer?.name}</p>
                          {assignedOrders[0]?.customer?.phone && (
                            <a href={`tel:${assignedOrders[0].customer.phone}`} className="mt-2 inline-flex items-center gap-2 text-sm text-blue-700">
                              <Phone className="w-4 h-4" /> Call customer
                            </a>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white p-3">
                          <p className="text-xs text-gray-500 mb-2">OTP Verification</p>
                          <input
                            placeholder="Enter OTP (UI only)"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          />
                          <p className="mt-2 text-[11px] text-gray-500">Use OTP at doorstep to confirm delivery.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {activeTab === 'history' && (
              <section className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">Order History</h2>
              <button
                onClick={exportHistoryCsv}
                className="text-xs px-2.5 py-1.5 rounded-lg bg-indigo-100 text-indigo-700 flex items-center gap-1"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            <div className="flex items-center gap-2 mb-4">
              {([
                { id: 'today', label: 'Today' },
                { id: 'week', label: 'Week' },
                { id: 'month', label: 'Month' },
              ] as const).map((range) => (
                <button
                  key={range.id}
                  onClick={() => setHistoryRange(range.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    historyRange === range.id
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
            {filteredHistory.length === 0 ? (
              <p className="text-sm text-gray-500">No matching history.</p>
            ) : (
              <div className="space-y-2">
                {filteredHistory.slice(0, 30).map((order) => (
                  <div key={`${order.id}-${order.status}`} className="rounded-lg bg-gray-50 p-3 text-sm flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{order.orderNumber}</p>
                      <p className="text-xs text-gray-500">{order.restaurant?.name || 'Restaurant'} • {order.customer?.name || 'Customer'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600">{formatStatus(order.status)}</p>
                      <p className="text-xs text-green-700">Rs {Number(order.deliveryFee || 0).toFixed(0)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </section>
            )}

            {activeTab === 'insights' && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Today's Performance</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Delivered Today</span><span className="font-semibold">{deliveredToday.length}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Earnings Today</span><span className="font-semibold text-emerald-700">Rs {deliveredToday.reduce((sum, order) => sum + Number(order.deliveryFee || 0), 0).toFixed(0)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Avg Earning / Trip</span><span className="font-semibold">Rs {avgEarningPerTrip.toFixed(1)}</span></div>
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Operational Insights</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="flex justify-between text-gray-600 mb-1"><span>Acceptance Rate</span><span>{acceptanceRate.toFixed(0)}%</span></div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500" style={{ width: `${Math.max(3, Math.min(100, acceptanceRate))}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-gray-600 mb-1"><span>Active Load</span><span>{assignedOrders.length} trips</span></div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500" style={{ width: `${Math.max(5, Math.min(100, assignedOrders.length * 20))}%` }} />
                  </div>
                </div>
                <p className="text-xs text-gray-500">Tip: Use ETA sort + Near filter during peak time to complete more trips.</p>
              </div>
            </div>
              </section>
            )}

            {activeTab === 'profile' && (
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3 gap-3">
                <h2 className="font-semibold text-gray-900">Delivery Partner Profile</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Name:</span>
                  <span className="text-gray-900">{user.name}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Email:</span>
                  <span className="text-gray-900">{user.email}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Phone:</span>
                  <span className="text-gray-900">{user.phone || '-'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Application Status:</span>
                  <span className="text-gray-900">{application?.status || 'NOT_SUBMITTED'}</span>
                </div>
                <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500 mb-2">Vehicle & Area</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-white border border-gray-100 p-3">
                      <p className="text-[11px] text-gray-500">Vehicle</p>
                      <p className="text-sm font-semibold text-gray-900">{application?.vehicleType || profile.vehicleType}</p>
                    </div>
                    <div className="rounded-lg bg-white border border-gray-100 p-3">
                      <p className="text-[11px] text-gray-500">Area</p>
                      <p className="text-sm font-semibold text-gray-900">{application?.deliveryArea || profile.deliveryArea || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-indigo-100 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3 gap-3">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-green-600" /> Application Summary
                </h2>
                {onOpenApplication && (
                  <button
                    type="button"
                    onClick={onOpenApplication}
                    className="inline-flex items-center gap-2 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                    title="Edit delivery partner application"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Application
                  </button>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Vehicle:</span> <span className="text-gray-900">{application?.vehicleType || profile.vehicleType}</span></p>
                <p><span className="text-gray-500">Vehicle Number:</span> <span className="text-gray-900">{application?.vehicleNumber || profile.vehicleNumber || '-'}</span></p>
                <p><span className="text-gray-500">Area:</span> <span className="text-gray-900">{application?.deliveryArea || profile.deliveryArea || '-'}</span></p>
                <p><span className="text-gray-500">Activated:</span> <span className="text-gray-900">{application?.activatedAt ? new Date(application.activatedAt).toLocaleString() : 'Pending'}</span></p>
              </div>
            </div>
              </section>
            )}
          </div>
        </section>
      </main>

      <button
        onClick={fetchCurrentLocation}
        className="fixed bottom-4 right-4 z-20 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-3 shadow-lg"
        title="Update current location"
      >
        <LocateFixed className="w-5 h-5" />
      </button>
    </div>
  );
}

